import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Star, ShieldCheck, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { captureTracking, getSessionId } from '@/lib/tracking';
import { useFunnel } from '@/context/FunnelContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Landing() {
  const navigate = useNavigate();
  const { setAnswer, resetAnswers } = useFunnel();
  const [hooks, setHooks] = useState({ hook1: '', hook2: '' });
  const [loaded, setLoaded] = useState(false);
  const [zip, setZip] = useState('');
  const [error, setError] = useState('');
  const [geo, setGeo] = useState({ city: '', state: '' });

  useEffect(() => {
    let mounted = true;

    // Capture Google Ads attribution params + record a (de-duped) click.
    const tracking = captureTracking(window.location.search);
    const sessionId = getSessionId();
    api
      .post('/track/click', {
        session_id: sessionId,
        landing_path: window.location.pathname,
        campaign_id: tracking.campaign_id,
        adgroup_id: tracking.adgroup_id,
        ad_id: tracking.ad_id,
        keyword: tracking.keyword,
        gclid: tracking.gclid,
        params: tracking.params,
      })
      .catch(() => {});

    // Fetch hooks, targeted by campaign / ad group / ad when present.
    const qs = new URLSearchParams({
      campaign: tracking.campaign_id || '',
      adgroup: tracking.adgroup_id || '',
      ad: tracking.ad_id || '',
    }).toString();
    api
      .get(`/config/public?${qs}`)
      .then((res) => {
        if (mounted) {
          setHooks({ hook1: res.data.hook1, hook2: res.data.hook2 });
          // stash detected geo for later funnel steps (address verification)
          setGeo({ city: res.data.city, state: res.data.state });
        }
      })
      .catch(() => {
        if (mounted)
          setHooks({
            hook1: 'Garage Door Repair Program',
            hook2: 'See if your home qualifies for same-day garage door service.',
          });
      })
      .finally(() => mounted && setLoaded(true));
    return () => {
      mounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = (e) => {
    e.preventDefault();
    const clean = zip.trim();
    if (!/^\d{5}$/.test(clean)) {
      setError('Please enter a valid 5-digit ZIP code.');
      return;
    }
    setError('');
    resetAnswers();
    setAnswer('zip', clean);
    setAnswer('city', geo.city);
    setAnswer('state', geo.state);
    navigate('/flow/homeowner');
  };

  return (
    <div
      className="min-h-full flex flex-col items-center justify-center text-center px-4 pt-[clamp(12px,3vh,40px)] pb-[clamp(88px,15vh,170px)]"
      data-testid="page-landing"
    >
      <div
        className={`w-full max-w-3xl mx-auto transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <h1
          className="font-slab font-bold tracking-tight text-slate-900 leading-[1.07] text-[clamp(1.9rem,5.2vw,3.6rem)]"
          data-testid="hero-hook1"
        >
          {hooks.hook1 || '\u00a0'}
        </h1>
        <p
          className="mt-[clamp(8px,1.6vh,18px)] text-slate-600 max-w-2xl mx-auto text-[clamp(0.95rem,1.7vw,1.125rem)]"
          data-testid="hero-hook2"
        >
          {hooks.hook2 || '\u00a0'}
        </p>
      </div>

      <form
        onSubmit={start}
        className="mt-[clamp(16px,3vh,40px)] mx-auto w-full max-w-2xl bg-white rounded-2xl shadow-[0_10px_30px_rgba(15,23,42,0.10)] border border-slate-200 p-[clamp(12px,1.6vh,20px)]"
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch">
          <Input
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
            inputMode="numeric"
            placeholder="Zip Code"
            aria-label="Zip Code"
            className="h-12 text-base rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-sky-300 sm:flex-1"
            data-testid="hero-zip-input"
          />
          <Button
            type="submit"
            className="h-12 px-7 rounded-xl font-semibold text-base bg-[#EF4444] hover:bg-[#DC2626] text-white shadow-sm transition-colors"
            data-testid="hero-get-started-button"
          >
            Get Started Now
          </Button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600 text-left" data-testid="hero-zip-error">
            {error}
          </p>
        )}
      </form>

      <div
        className="mt-[clamp(10px,1.8vh,20px)] flex items-center justify-center gap-2 text-sm font-medium text-blue-600"
        data-testid="hero-trust-line"
      >
        <Wrench className="h-4 w-4" />
        <span>Takes 2 minutes to check eligibility</span>
      </div>

      <div className="mt-[clamp(10px,1.8vh,24px)] flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-slate-500">
        <span className="flex items-center gap-1.5">
          <Star className="h-4 w-4 text-amber-400 fill-amber-400" /> 5-Star Rated
        </span>
        <span className="flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-emerald-500" /> Licensed &amp; Insured
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-blue-500" /> Same-Day Service
        </span>
      </div>
    </div>
  );
}
